<template>
  <!-- Desktop: acțiuni grupate pe categorii, separate prin dividere verticale.
       Categorii: Inteligență · Comunicare · Gestionare · [Organizare via slot] · Primar + overflow.
       Vezi docs/design.md („Header actions — grupare") și docs/design/. -->
  <div class="ncrm-hdr-actions d-none d-md-flex align-center">
    <!-- Inteligență -->
    <div class="ncrm-hdr-group">
      <AiAssistLaunchButton
        color="secondary"
        variant="tonal"
        size="default"
        :entity-type="entityType"
        :entity-id="entityId"
        :profile-key="aiProfileKey"
        :label="aiLabel"
        :disabled="disabled"
        @error="$emit('error', $event)"
      />
    </div>

    <span class="ncrm-hdr-div" aria-hidden="true" />

    <!-- Comunicare (Telegram rămâne vizibil) -->
    <div class="ncrm-hdr-group">
      <v-btn color="neutral" variant="tonal" size="default" :disabled="disabled" @click="companyEmailDialogOpen = true">
        <v-icon start>mdi-email-fast-outline</v-icon>
        {{ $t("ncrm.integrations.companyEmail.send.action") }}
      </v-btn>
      <v-btn
        color="neutral"
        variant="tonal"
        size="default"
        :disabled="disabled"
        :aria-label="$t('ncrm.integrations.telegram.send.actionLabel')"
        @click="sendDialogOpen = true"
      >
        <v-icon start>mdi-send</v-icon>
        {{ $t("ncrm.integrations.telegram.send.action") }}
      </v-btn>
      <v-btn
        v-if="entityType === 'person'"
        color="neutral"
        variant="text"
        size="default"
        :disabled="disabled"
        @click="inviteDialogOpen = true"
      >
        <v-icon start>mdi-account-arrow-right</v-icon>
        {{ $t("ncrm.integrations.telegram.invite.action") }}
      </v-btn>
    </div>

    <span class="ncrm-hdr-div" aria-hidden="true" />

    <!-- Gestionare -->
    <div class="ncrm-hdr-group">
      <v-btn color="primary" variant="outlined" size="default" :disabled="disabled" @click="$emit('edit')">
        <v-icon start>mdi-pencil</v-icon>
        {{ $t("ncrm.contacts.details.actions.edit") }}
      </v-btn>
    </div>

    <!-- Organizare (ex. board-uri) — injectat de pagina-gazdă -->
    <template v-if="$slots.organize">
      <span class="ncrm-hdr-div" aria-hidden="true" />
      <div class="ncrm-hdr-group">
        <slot name="organize" />
      </div>
    </template>

    <span class="ncrm-hdr-div" aria-hidden="true" />

    <!-- Acțiune primară + overflow -->
    <div class="ncrm-hdr-group">
      <v-btn color="primary" variant="elevated" size="default" :disabled="disabled" @click="$emit('add-activity')">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.activity.actions.add") }}
      </v-btn>
      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            size="default"
            icon="mdi-dots-vertical"
            class="ncrm-header-more-btn"
            :disabled="disabled"
            :aria-label="$t('ncrm.contacts.details.actions.more')"
          />
        </template>
        <v-list density="compact" class="py-1">
          <NcrmCompanyConversionAction v-if="entityType === 'company'" :ids="[entityId]" menu :disabled="disabled" />
          <v-list-item @click="$emit('delete')">
            <template #prepend>
              <v-icon color="error">mdi-delete</v-icon>
            </template>
            <v-list-item-title class="text-error">
              {{ $t("ncrm.contacts.details.actions.delete") }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>

  <!-- Mobil: AI + acțiunea primară + meniul ⋮ cu restul categoriilor -->
  <div class="d-flex d-md-none align-center ga-2">
    <AiAssistLaunchButton
      color="secondary"
      variant="tonal"
      size="default"
      :entity-type="entityType"
      :entity-id="entityId"
      :profile-key="aiProfileKey"
      :label="aiLabel"
      :disabled="disabled"
      @error="$emit('error', $event)"
    />
    <v-btn color="primary" variant="elevated" size="default" :disabled="disabled" @click="$emit('add-activity')">
      <v-icon start>mdi-plus</v-icon>
      {{ $t("ncrm.activity.actions.add") }}
    </v-btn>
    <v-menu location="bottom end">
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          size="default"
          icon="mdi-dots-vertical"
          class="ncrm-header-more-btn"
          :disabled="disabled"
          :aria-label="$t('ncrm.contacts.details.actions.more')"
        />
      </template>
      <v-list density="compact" class="py-1">
        <NcrmCompanyConversionAction v-if="entityType === 'company'" :ids="[entityId]" menu :disabled="disabled" />
        <v-list-item @click="companyEmailDialogOpen = true">
          <template #prepend>
            <v-icon color="primary">mdi-email-fast-outline</v-icon>
          </template>
          <v-list-item-title>
            {{ $t("ncrm.integrations.companyEmail.send.action") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="sendDialogOpen = true">
          <template #prepend>
            <v-icon>mdi-send</v-icon>
          </template>
          <v-list-item-title>
            {{ $t("ncrm.integrations.telegram.send.action") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="entityType === 'person'" @click="inviteDialogOpen = true">
          <template #prepend>
            <v-icon>mdi-account-arrow-right</v-icon>
          </template>
          <v-list-item-title>
            {{ $t("ncrm.integrations.telegram.invite.action") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="$emit('edit')">
          <template #prepend>
            <v-icon color="primary">mdi-pencil</v-icon>
          </template>
          <v-list-item-title>
            {{ $t("ncrm.contacts.details.actions.edit") }}
          </v-list-item-title>
        </v-list-item>
        <template v-if="$slots.organizeMenu">
          <v-divider class="my-1" />
          <slot name="organizeMenu" />
        </template>
        <v-divider class="my-1" />
        <v-list-item @click="$emit('delete')">
          <template #prepend>
            <v-icon color="error">mdi-delete</v-icon>
          </template>
          <v-list-item-title class="text-error">
            {{ $t("ncrm.contacts.details.actions.delete") }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>

  <NcrmCompanyEmailSendDialog
    v-model="companyEmailDialogOpen"
    :entity-type="entityType"
    :entity-id="entityId"
    :recipient-email="recipientEmail"
    :recipient-name="recipientName"
    @notify="$emit('notify', $event)"
    @sent="$emit('email-sent', $event)"
  />
  <TelegramSendDialog
    v-model="sendDialogOpen"
    :entity-type="entityType"
    :entity-id="entityId"
    @notify="$emit('notify', $event)"
  />
  <TelegramInviteContactDialog
    v-if="entityType === 'person'"
    v-model="inviteDialogOpen"
    :person-id="entityId"
  />
</template>

<script>
import NcrmCompanyConversionAction from "@/components/ncrm/contacts/NcrmCompanyConversionAction.vue";
import AiAssistLaunchButton from "@/components/ai/AiAssistLaunchButton.vue";
import NcrmCompanyEmailSendDialog from "@/components/ncrm/shared/NcrmCompanyEmailSendDialog.vue";
import TelegramSendDialog from "@/components/integrations/telegram/TelegramSendDialog.vue";
import TelegramInviteContactDialog from "@/components/integrations/telegram/TelegramInviteContactDialog.vue";

export default {
  name: "NcrmContactHeaderActions",
  components: {
    NcrmCompanyConversionAction,
    AiAssistLaunchButton,
    NcrmCompanyEmailSendDialog,
    TelegramSendDialog,
    TelegramInviteContactDialog,
  },
  emits: ["error", "notify", "email-sent", "add-activity", "edit", "delete"],
  data() {
    return { companyEmailDialogOpen: false, sendDialogOpen: false, inviteDialogOpen: false };
  },
  props: {
    entityType: {
      type: String,
      required: true,
    },
    entityId: {
      type: [String, Number],
      required: true,
    },
    aiProfileKey: {
      type: String,
      required: true,
    },
    aiLabel: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    recipientEmail: {
      type: String,
      default: "",
    },
    recipientName: {
      type: String,
      default: "",
    },
  },
};
</script>

<style scoped>
.ncrm-hdr-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  row-gap: 10px;
}

.ncrm-hdr-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ncrm-hdr-div {
  width: 1px;
  align-self: stretch;
  min-height: 28px;
  margin: 0 3px;
  background: var(--ncrm-color-border-strong);
}
</style>
